import Pdf from "../Pdf";
import Patient from "../Crud/Patient";
import moment from "moment";
import measurementsInSitting from "../../Asset/Image/measurements_in_sitting.png?inline";
import User from "../Crud/User";
export default class PdfAssistiveTechnologyReport extends Pdf {
    /**
     * Constructs a new PdfAssistiveTechnologyReport instance.
     * @param {Object} data - Data for the report.
     * @param {Object} options - Options for the PDF.
     */
    constructor(data = {}, options = {}) {
        super(data, options);
    }


    /**
     * Renders the PDF document.
     */
    async render() {
        await this.fontLoadPromise;
        let x = this.margin.left;
        let y = this.document.internal.pageSize.height;
        const chartContext = this.data.chartContext || {};
        const mainContext = this.data.mainContext || {};

        const atpAssessment = Object.values(chartContext.originalChart.atpAssessment).find((atpAssessment) => {
            return atpAssessment.orderActivityId === chartContext.orderId;
        }) || {};

        const minSectionHeightEquipmentSection = 250;
        const minSectionHeightMedicalHistory = 120;
        const minSectionHeightMeasurements = 245 + this.margin.bottom;
        const minSectionHeightAdditionalComments = 120;
        const minSectionHeightHomeAssessment = 245 + this.margin.bottom;

        y = this.renderHeader({
            title: "Assistive Technology Report",
        });
        y += 50;
        this.renderText("Evaluation Date:", "", x, y, 20, { fontSize: 12, labelFontStyle: "bold" });
        this.renderText("", moment(atpAssessment.evaluationDate).format("M/D/YYYY"), x + 80, y, 20, { fontSize: 12, valueFontStyle: "normal" });
        y += 20;
        y = this.renderPatientInformationSection(y);
        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightMedicalHistory);
        y = this.renderMedicalHistorySection(y);

        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightMeasurements);
        this.renderImageSection(y);
        this.renderAdditionalMeasurementsSection(y);
        y = this.renderPrimaryMeasurementsSection(y);
        // Render items required for chair.
        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightHomeAssessment);
        y = this.renderHomeAssessmentSection(y);

        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightAdditionalComments);
        y = this.renderAdditionalCommentsSection(y);

        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightEquipmentSection);
        y = this.renderEquipmentSection(y);

        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, 200);
        y = await this.renderSignatureSection(y);

        this.renderPageNumbers();
    }
    /**
     * Renders the Patient Information section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderPatientInformationSection(y) {
        const chartContext = this.data.chartContext || {};
        const patient = chartContext.originalChart.patient[chartContext.patientId] || {};
        const columnGap = 200;
        const spacing = 20;
        const lineHeight = 15;

        const patientInformation = [
            { label: 'Name', data: `${Patient.getDisplayName(patient)}` },
            { label: 'Address', data: `${(patient.address + "," || '')}` },
            { label: 'Gender', data: `${(patient.gender || '')}` },
            { label: '', data: `${(patient.city + "," || '')} ${(patient.state + "," || '')} ${(patient.zipCode || '')}` },
            { label: 'Date of Birth', data: `${(patient.dateOfBirth || '')}` },
            { label: 'Height / Weight', data: `${Math.floor(patient.height / 12)}' ${patient.height % 12}\" / ${(patient.weight || '')} lbs` },
        ];
        this.renderSectionTitle("Patient", null, y);
        y += spacing;
        y = this.renderGrid(patientInformation, false, { lineHeight: lineHeight, columnGap: columnGap, spacing: 80, fontSize: 10, fontStyle: "bold", yStart: y });
        this.renderSectionDivider(y);
        y += spacing; // space between section divider and next element
        return y;
    }

    /**
     * Renders the Medical History Section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderMedicalHistorySection(y) {
        const chartContext = this.data.chartContext || {};
        const atpAssessment = Object.values(chartContext.originalChart.atpAssessment).find((atpAssessment) => {
            return atpAssessment.orderActivityId === chartContext.orderId;
        }) || {};
        const spacing = 20;
        const x = this.margin.left;
        if (atpAssessment["narrative"] && atpAssessment["narrative"].trim() !== '') {
            this.renderSectionTitle("Medical History", null, y);
            y += spacing;
            this.renderText("Narrative", "", x, y, null, { fontSize: 10, fontStyle: "bold" });
            y += spacing;

            y = this.renderText("", atpAssessment["narrative"].trim(), x, y, null, { renderHeader: true, title: "Assistive Technology Report", fontSize: 10, valueFontStyle: "normal" });
            y += spacing;
            this.renderSectionDivider(y);
            y += spacing;
            return y;
        }
        return y;
    }

    /**
     * Renders the Primary Measurements section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderPrimaryMeasurementsSection(y) {
        const chartContext = this.data.chartContext || {};
        const x = this.margin.left;
        const labelWidth = 80;
        const columnWidth = 60;
        const spacing = 20;
        const lineHeight = 15;

        const measurementInSitting = Object.values(chartContext.originalChart.measurementInSitting || {})
            .find(m => m.orderActivityId === chartContext.orderId) || {};
        const measurementRowsA = [
            { key: "topOfHead", letter: "A", label: "Top of Head", left: "topOfHead", right: null, adornment: 'in' },
        ];

        const measurementRowsBToH = [
            { key: "topOfShoulder", letter: "B", label: "Top of Shoulder", left: "topOfShoulderLeft", right: "topOfShoulderRight", adornment: 'in' },
            { key: "axilla", letter: "C", label: "Axilla", left: "axillaLeft", right: "axillaRight", adornment: 'in' },
            { key: "seatDepth", letter: "D", label: "Seat Depth", left: "seatDepthLeft", right: "seatDepthRight", adornment: 'in' },
            { key: "lowerLegLength", letter: "E", label: "Lower Leg Length", left: "lowerLegLengthLeft", right: "lowerLegLengthRight", adornment: 'in' },
            { key: "footLength", letter: "F", label: "Foot Length", left: "footLengthLeft", right: "footLengthRight", adornment: 'in' },
            { key: "footWidth", letter: "G", label: "Foot Width", left: "footWidthLeft", right: "footWidthRight", adornment: 'in' },
            { key: "seatToElbow", letter: "H", label: "Seat to Elbow", left: "seatToElbowLeft", right: "seatToElbowRight", adornment: 'in' },
        ];
        const measurementRowsIToK = [
            { key: "hipWidth", letter: "I", label: "Hip Width", left: "hipWidth", right: null, adornment: 'in' },
            { key: "chestWidth", letter: "J", label: "Chest Width", left: "chestWidth", right: null, adornment: 'in' },
            { key: "shoulderWidth", letter: "K", label: "Shoulder Width", left: "shoulderWidth", right: null, adornment: 'in' },
        ];
        this.renderSectionTitle("Measurement In Sitting", null, y);
        y += spacing;
        this.renderText(measurementRowsA[0].letter, "", x, y, labelWidth, { fontSize: 10, labelFontStyle: "bold" });
        this.renderText(measurementRowsA[0].label, "", x + 10, y, labelWidth, { fontSize: 10, labelFontStyle: "bold" });
        const leftValue = measurementInSitting[measurementRowsA[0].left] ?? '';
        const leftText = leftValue ? `${leftValue.toFixed(1)}${measurementRowsA[0].adornment ? ' ' + measurementRowsA[0].adornment : ''}` : '-';
        this.renderText("", leftText, x + labelWidth - (leftText === '-' ? 10 : 0), y, columnWidth, { align: "right", valueFontStyle: "normal" });
        y += lineHeight * 2;

        this.renderText("Left", "", x + labelWidth + 40, y, null, { align: "center", fontSize: 10, labelFontStyle: "normal" });
        this.renderText("Right", "", x + labelWidth + 76, y, null, { align: "center", fontSize: 10, labelFontStyle: "normal" });

        y += lineHeight;
        // Render each measurement row under the headers
        measurementRowsBToH.forEach(row => {
            // Label
            this.renderText(row.letter, "", x, y, labelWidth, { labelFontStyle: "bold", fontSize: 10 });
            this.renderText(row.label, "", x + 10, y, labelWidth, { labelFontStyle: "bold", fontSize: 10 });
            // Left value
            let leftText = '';
            if (row.left) {
                const leftValue = measurementInSitting[row.left] ?? '';
                leftText = leftValue ? `${leftValue.toFixed(1)}${row.adornment ? ' ' + row.adornment : ''}` : '-';
                this.renderText("", leftText, x + labelWidth - (leftText === "-" ? 10 : 0), y, columnWidth, { align: "right", valueFontStyle: "normal" });
            }
            // Right value (if applicable)
            let rightText = '';
            if (row.right) {
                const rightValue = measurementInSitting[row.right] ?? '';
                rightText = rightValue ? `${rightValue.toFixed(1)}${row.adornment ? ' ' + row.adornment : ''}` : '-';
                this.renderText("", rightText, x + labelWidth + 40 - (rightText === "-" ? 10 : 0), y, columnWidth, { align: "right", valueFontStyle: "normal" });
            }
            y += lineHeight;
        });
        y += 10; // Extra spacing between sections
        measurementRowsIToK.forEach(row => {
            // Label
            this.renderText(row.letter, "", x, y, labelWidth, { fontSize: 10, labelFontStyle: "bold" });
            this.renderText(row.label, "", x + 10, y, labelWidth, { fontSize: 10, labelFontStyle: "bold" });

            // Left value
            let leftText = '';
            if (row.left) {
                const leftValue = (measurementInSitting[row.left] ?? '');
                leftText = leftValue ? `${leftValue.toFixed(1)}${row.adornment ? ' ' + row.adornment : ''}` : '-';
                this.renderText("", leftText, x + labelWidth - (leftText === "-" ? 10 : 0), y, columnWidth, { align: "right", fontSize: 10, valueFontStyle: "normal" });
            }
            // Right value (if applicable)
            let rightText = '';
            if (row.right) {
                const rightValue = (measurementInSitting[row.right] ?? '');
                rightText = rightValue ? `${rightValue.toFixed(1)}${row.adornment ? ' ' + row.adornment : ''}` : '-';
                this.renderText("", rightText, x + labelWidth + 40 - (rightText === "-" ? 10 : 0), y, columnWidth, { align: "right", fontSize: 10, valueFontStyle: "normal" });
            }
            y += lineHeight;
        });
        this.renderSectionDivider(y);
        return y + spacing;
    }

    /**
     * Renders the Home Assessment section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderHomeAssessmentSection(y) {
        const chartContext = this.data.chartContext || {};
        const spacing = 20;
        const lineHeight = 15;

        const deliveryInformation = chartContext.originalChart?.deliveryInformation || {};
        const homeAssessmentEntry = Object.values(deliveryInformation).sort((a, b) => b.id - a.id).find(
            (d) =>
                d &&
                d.orderId === chartContext.orderId &&
                (d.documentType === "Home Assessment" || d.documentType === "Home Assessment Web") &&
                typeof d.documentInfoPruned === "string" &&
                !d.documentInfoPruned.includes("Home Assessment Completed")
        );

        let homeAssessmentDocument = {};
        if (homeAssessmentEntry && typeof homeAssessmentEntry.documentInfoPruned === "string") {
            try {
                homeAssessmentDocument = JSON.parse(homeAssessmentEntry.documentInfoPruned);
            } catch (e) {
                homeAssessmentDocument = {};
            }
        }

        const minSectionHeightHomeAssessmentFlooringRows = 40;
        const minSectionHeightHomeAssessmentRoomMeasurementRows = 125;

        this.renderSectionTitle("Home Assessment", null, y);
        y += spacing;
        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightHomeAssessmentFlooringRows);
        y = this.renderHomeAssessmentFlooringRows(y, homeAssessmentDocument);
        y += lineHeight;
        y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightHomeAssessmentRoomMeasurementRows);
        y = this.renderHomeAssessmentRoomMeasurementRows(y, homeAssessmentDocument);
        y += lineHeight;
        y = this.renderHomeAssessmentEquipmentQuestionRows(y, homeAssessmentDocument);

        this.renderSectionDivider(y);
        return y + spacing;
    }

    /**
     * Renders the Home Assessment Flooring Rows.
     * @param {*} y
     * @param {*} homeAssessmentDocument
     * @returns
     */
    renderHomeAssessmentFlooringRows(y, homeAssessmentDocument) {
        const x = this.margin.left;
        const labelWidth = 50;
        const columnWidth = 60;
        const lineHeight = 15;

        const homeAssessmentFlooringTypeKeys = ["carpet", "linoleum", "woodLaminate", "tile", "flooringOther"];

        const homeAssessmentFlooringRows = [
            { key: "flooringType", label: "Flooring Type", left: homeAssessmentFlooringTypeKeys },
            { key: "flooringSuitableForPowerMobilityDevice", label: "Flooring suitable for power mobility device?", left: "flooringMobilityAppropriate", right: "flooringComments" },
        ];

        // render flooring type row
        this.renderText(homeAssessmentFlooringRows[0].label, "", x, y, labelWidth, { labelFontSize: 10, labelFontStyle: "bold" });
        const leftValueFlooringType = homeAssessmentFlooringRows[0].left.map(key => {
            if (homeAssessmentDocument[key] === true) {
                return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim();
            }

            if (typeof homeAssessmentDocument[key] === 'string' && homeAssessmentDocument[key].trim() !== '') {
                return homeAssessmentDocument[key].trim();
            }

            return null;
        }).filter(Boolean).join(", ");

        const leftTextFlooringType = leftValueFlooringType ? `${leftValueFlooringType}` : '';
        y = this.renderText("", leftTextFlooringType, x + 20 - (leftTextFlooringType === '' ? 10 : 0), y, columnWidth, { align: "left", valueFontSize: 10, valueFontStyle: "normal" });
        y += lineHeight;

        // render flooring suitable for power mobility device row
        this.renderText(homeAssessmentFlooringRows[1].label, "", (x), y, labelWidth, { labelFontSize: 10, labelFontStyle: "bold" });
        const leftValueFlooringSuitable = homeAssessmentFlooringRows[1].left ? (homeAssessmentDocument[homeAssessmentFlooringRows[1].left] === true ? "Yes" : "No") : '';
        const leftTextFlooringSuitable = leftValueFlooringSuitable ? `${leftValueFlooringSuitable}` : '';
        this.renderText("", leftTextFlooringSuitable, (x + 160) - (leftTextFlooringSuitable === '' ? 10 : 0), y, columnWidth, { align: "left", valueFontSize: 10, valueFontStyle: "normal" });
        y += lineHeight;

        // render flooring suitable for power mobility device reason row
        const rightValueFlooringSuitableReason = homeAssessmentFlooringRows[1].right ? (homeAssessmentDocument[homeAssessmentFlooringRows[1].right] || '') : '';
        const rightTextFlooringSuitableReason = rightValueFlooringSuitableReason ? `${rightValueFlooringSuitableReason.trim()}` : '';
        y = this.renderText("", rightTextFlooringSuitableReason, x, y, null, { renderHeader: true, title: "Assistive Technology Report", valueFontStyle: "normal", fontSize: 10 });
        return y;
    }

    /**
     *  Renders the Home Assessment Room Measurement Rows.
     * @param {*} y
     * @param {*} homeAssessmentDocument
     * @returns
     */
    renderHomeAssessmentRoomMeasurementRows(y, homeAssessmentDocument) {
        const x = this.margin.left;
        const labelWidth = 50;
        const columnWidth = 60;
        const lineHeight = 15;

        const homeAssessmentRoomMeasurementRows = [
            { key: "kitchenFoodPrep", label: "Kitchen/Food Prep", left: "kitchenThresholdWidth", right: "kitchenDimensionY", center: "kitchenDimensionX", adornmentLeft: 'in', adornmentRight: 'ft', adornmentCenter: 'ft' },
            { key: "bedroomSleeping", label: "Bedroom/Sleeping", left: "bedroomThresholdWidth", right: "bedroomDimensionY", center: "bedroomDimensionX", adornmentLeft: 'in', adornmentRight: 'ft', adornmentCenter: 'ft' },
            { key: "bathroom", label: "Bathroom", left: "bathroomThresholdWidth", right: "bathroomDimensionY", center: "bathroomDimensionX", adornmentLeft: 'in', adornmentRight: 'ft', adornmentCenter: 'ft' },
            { key: "livingRoom", label: "Living Room", left: "livingRoomThresholdWidth", right: "livingRoomDimensionY", center: "livingRoomDimensionX", adornmentLeft: 'in', adornmentRight: 'ft', adornmentCenter: 'ft' },
            { key: "connectionHallways", label: "Connecting Hallways", left: "hallwaysThresholdWidth", right: "hallwaysDimensionY", center: "hallwaysDimensionX", adornmentLeft: 'in', adornmentRight: 'ft', adornmentCenter: 'ft' },
        ];

        y += lineHeight;
        // Render headers for room measurements
        this.renderText("Threshold Width", "", x + labelWidth + 60, y - lineHeight, columnWidth, { align: "center", fontSize: 8, labelFontStyle: "normal" });

        this.renderText("Room Dimensions", "", x + labelWidth + 135, y - lineHeight, columnWidth, { align: "center", fontSize: 8, labelFontStyle: "normal" });

        // Render each room measurement row under the headers
        homeAssessmentRoomMeasurementRows.forEach(row => {
            // Label
            this.renderText(row.label, "", x, y, labelWidth, { labelFontStyle: "bold", fontSize: 10 });
            // Left value
            let leftText = '';
            if (row.left) {
                const leftValue = homeAssessmentDocument[row.left] && homeAssessmentDocument[row.left] !== '' ? homeAssessmentDocument[row.left] : '';
                leftText = leftValue > 0 ? `${leftValue}${row.adornmentLeft ? ' ' + row.adornmentLeft : ''}` : '-';
                this.renderText("", leftText, (x + labelWidth + 40) - (leftText === "-" ? 10 : 0), y, columnWidth, { align: "right", fontSize: 10, valueFontStyle: "normal" });
            }
            // Right value (if applicable)
            let rightText = '';
            if (row.right) {
                const rightValue = homeAssessmentDocument[row.right] && homeAssessmentDocument[row.right] !== '' ? homeAssessmentDocument[row.right] : '';
                rightText = rightValue > 0 ? `${rightValue}${row.adornmentRight ? ' ' + row.adornmentRight : ''}` : '-';
                this.renderText("", rightText, x + labelWidth + 140 - (rightText === "-" ? 10 : 0), y, columnWidth, { align: "right", fontSize: 10, valueFontStyle: "normal" });
            }

            // Separator 'x'
            this.renderText("x", "", x + labelWidth + 165, y, columnWidth, { align: "center", fontSize: 10, labelFontStyle: "normal" });

            // Center value (if applicable)
            let centerText = '';
            if (row.center) {
                const centerValue = homeAssessmentDocument[row.center] && homeAssessmentDocument[row.center] !== '' ? homeAssessmentDocument[row.center] : '';
                centerText = centerValue > 0 ? `${centerValue}${row.adornmentCenter ? ' ' + row.adornmentCenter : ''}` : '-';
                this.renderText("", centerText, x + labelWidth + 90 - (centerText === "-" ? 5 : 0), y, columnWidth, { align: "right", fontSize: 10, valueFontStyle: "normal" });
            }

            y += lineHeight;
        });
        return y;
    }

    /**
     * Renders the Home Assessment Equipment Question Rows.
     * @param {*} y
     * @param {*} homeAssessmentDocument
     * @returns
     */
    renderHomeAssessmentEquipmentQuestionRows(y, homeAssessmentDocument) {
        const x = this.margin.left;
        const labelWidth = 50;
        const columnWidth = 60;
        const lineHeight = 15;
        const minSectionHeightPerQuestion = 40 + this.margin.bottom;

        const homeAssessmentEquipmentQuestionRows = [
            { key: "adequateAccessForMobilityDevice", label: "Adequate Access for Mobility Device?", left: ["adequateSpacePOV", "adequateSpacePWC"], leftSpacing: 80 + labelWidth, right: ["inadequateSpacePOVDescription", "inadequateSpacePWCDescription"] },
            { key: "otherIssues", label: "Other Issues", left: ["isOtherPWC", "isOtherPOV"], leftSpacing: 10, right: ["otherPOV", "otherPWC"] },
            { key: "adequateIngressRegress", label: "Adequate ingress/regress?", left: ["adequateIngressRegress"], leftSpacing: 30 + labelWidth, right: ["inadequateIngressRegress"] },
        ];

        // Render each equipment question row under the headers
        homeAssessmentEquipmentQuestionRows.forEach(row => {

            const rightValue = row.right
                .map(key => {
                    if (typeof homeAssessmentDocument[key] === 'string' && homeAssessmentDocument[key].trim() !== '') {
                        return homeAssessmentDocument[key];
                    }
                    return undefined;
                })
                .filter(value => typeof value === 'string' && value.trim() !== '');

            const hasTrueLeftValue = row.left.some(key => homeAssessmentDocument[key] !== false && homeAssessmentDocument[key] !== undefined && homeAssessmentDocument[key] !== null);
            // Represent the result as a single boolean element, to keep downstream usage (`leftValue[0]`) unchanged.
            const leftValue = row.left.length > 0 ? [hasTrueLeftValue] : [];

            this.renderText(row.label, "", x, y, labelWidth, { labelFontStyle: "bold", fontSize: 10 });

            // Left value
            let leftText = '';
            if (row.left) {
                leftText = leftValue.length > 0 ? `${leftValue[0] === true ? "Yes" : "No"}` : '';
                this.renderText("", leftText, x + (row.leftSpacing || 110) - (leftText === "" ? 10 : 0), y, columnWidth, { align: "left", fontSize: 10, valueFontStyle: "normal" });
                y += lineHeight;
            }

            let rightText = '';
            if (row.right && rightValue.length > 0) {
                rightText = rightValue.length > 0 ? `${rightValue[0].trim()}` : '';
                y = this.renderText("", rightText, x, y, null, { renderHeader: true, title: "Assistive Technology Report", fontSize: 10, valueFontStyle: "normal" });
            }
            y += lineHeight;
            if (rightValue.length > 0 && rightValue[0]) {
                y = this.renderAdditionalPageIfNeeded(y, { renderHeader: true, title: "Assistive Technology Report" }, minSectionHeightPerQuestion);
            }
        });
        return y;
    }

    /**
     * Renders the Additional Measurements section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderAdditionalMeasurementsSection(y) {
        const chartContext = this.data.chartContext || {};
        const labelWidth = 40;
        const columnWidth = 80;
        const spacing = 20;
        const lineHeight = 15;
        const x = this.margin.left + 190;

        const measurementInSitting = Object.values(chartContext.originalChart.measurementInSitting || {})
            .find(m => m.orderActivityId === chartContext.orderId) || {};

        const additionalMeasurementRows = [
            { key: "headWidth", label: "Head Width", adornment: 'in' },
            { key: "headDepth", label: "Head Depth", adornment: 'in' },
            { key: "neckWidth", label: "Neck Width", adornment: 'in' },
            { key: "neckCircumference", label: "Neck Circumference", adornment: 'in' },
            { key: "inferiorAngleOfScapula", label: "Inferior Angle of Scapula", adornment: 'in' },
            { key: "chestDepth", label: "Chest Depth", adornment: 'in' },
        ];

        y += spacing;
        additionalMeasurementRows.forEach(row => {
            // Label
            this.renderText(row.label, "", x, y, labelWidth, { labelFontStyle: "bold", fontSize: 10 });
            // Left value
            let leftText = '';
            if (row.key) {
                const leftValue = measurementInSitting[row.key] ?? '';
                leftText = leftValue ? `${leftValue.toFixed(1)}${row.adornment ? ' ' + row.adornment : ''}` : '-';
            }
            this.renderText("", leftText, x + labelWidth + 40 - (leftText === "-" ? 10 : 0), y, columnWidth, { align: "right", valueFontStyle: "normal", fontSize: 10 });
            y += lineHeight;
        });
        return y;
    }

    /**
     * Renders the Additional Comments Section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderAdditionalCommentsSection(y) {
        const chartContext = this.data.chartContext || {};
        const measurementInSitting = Object.values(chartContext.originalChart.measurementInSitting || {})
            .find(m => m.orderActivityId === chartContext.orderId) || {};

        const spacing = 20;
        const x = this.margin.left;
        if (measurementInSitting["additionalComments"] && measurementInSitting["additionalComments"].trim() !== '') {
            this.renderSectionTitle("Additional Comments", null, y);
            y += spacing;
            y = this.renderText("", measurementInSitting["additionalComments"].trim(), x, y, null, { renderHeader: true, title: "Assistive Technology Report", fontSize: 10, valueFontStyle: "normal" });
            this.renderSectionDivider(y);
            y += spacing; // space between section divider and next element
            return y;
        }
        return y;
    }

    /**
     * Renders the Equipment section.
     * @param {number} y - The vertical position to start rendering.
     * @returns {number} The updated vertical position after rendering.
     */
    renderEquipmentSection(y) {
        const mainContext = this.data.mainContext || {};
        const chartContext = this.data.chartContext || {};
        const spacing = 20;

        const products = [];
        Object.values(chartContext.originalChart.orderActivityProducts || {}).find(orderActivityProduct => {
            if (orderActivityProduct.orderActivityId === chartContext.orderId) {
                products.push(chartContext.originalChart.product[orderActivityProduct.productsId]);
            }
        }) || [];

        const productData = (products || [])
            .filter(product => product.billable === true)
            .map(product => [
                mainContext.productSubTypesIndexed?.[product.productSubTypeId]?.name || "Product Description",
                product.hcpcs,
                product.qty,
            ]);

        this.renderSectionTitle("Equipment", null, y);
        y += spacing; // space between section title and next element
        y = this.renderTable({
            columns: products.length === 0
                ? [
                    { label: "Item Description", columnWidth: 600 }
                ]
                : [
                    { label: "Item Description", columnWidth: 400 },
                    { label: "HCPCS", columnWidth: 100 },
                    { label: "Qty", columnWidth: 100 },
                ],
            rows: productData.length <= 0 ? Array.from({ length: 1 }, () => [""]) : productData,
            x: this.margin.left,
            y: y,
            renderHeader: true,
            title: "Assistive Technology Report",
        });
        return y;
    }

    /**
     * Renders the signature section of the PDF.
     * @param {number} y - The vertical position to start rendering the signature section.
     * @returns {number} The updated vertical position after rendering the signature section.
     */
    async renderSignatureSection(y) {
        const chartContext = this.data.chartContext || {};
        const mainContext = this.data.mainContext || {};
        const documentSignature = Object.values(chartContext.chart.documentSignature).find((documentSignature) => {
            return documentSignature.id < 0;
        }) || null;

        const signatureData = Object.values(mainContext.signaturesIndexed).find((signature) => signature.id === documentSignature?.signatureId)

        let signature = signatureData?.data ? signatureData.data : "";
        let date = documentSignature?.createdAt ? moment(documentSignature.createdAt).format("M/D/YYYY") : moment().format("M/D/YYYY");
        // 133 is the Term Id for typed signatures.
        const fontOptions = signatureData?.signatureMethodTermId === 133 ? {
            labelFontSize: 10,
            valueFontSize: 24,
            labelFontStyle: "normal",
            valueFont: "Tangerine",
            valueFontStyle: "normal"
        } : undefined;
        const signedByUser = User.getDisplayName(mainContext.usersIndexed[signatureData?.userprofileId]) || "";
        let width = this.document.internal.pageSize.width - (this.margin.left + this.margin.right);
        let height = this.document.internal.pageSize.height - (this.margin.top + this.margin.bottom);
        const x = this.margin.left;
        y = this.renderText(
            "",
            `I have evaluated this patient and have determined that the listed equipment is medically necessary to meet these goals and to reach these outcomes. This evaluation and recommendation have been reviewed with the patient.`,
            x, height - 80,
            null);

        y = await this.renderInput(
            "ATP Signature",
            signature,
            x,
            height - 5,
            width * 0.6,
            fontOptions
        );
        this.renderText(
            "",
            `Electronically signed by ${signedByUser}`,
            x,
            height + 24,
            null,
            { fontSize: 10 }
        );
        y = await this.renderInput(
            "Date",
            date,
            x + (width * 0.7),
            height - 5,
            width * 0.3
        );
        return y;
    }

    /**
     * Renders the Measurement Diagram image section.
     * @param {number} y - The vertical position to start rendering.
     */
    renderImageSection(y) {
        const width = 180;
        let height = 142;
        this.document.addImage(measurementsInSitting, "PNG", this.margin.left + 360, y, width, height);
        return y + height;
    }

    renderGrid(labelsData, enableInputs = true, options = {}) {
        const margin = this.margin;
        const defaultOptions = {
            lineHeight: 20,
            columnGap: 100,
            yStart: 110,
            x2Start: margin.left,
            spacing: margin.left * 3,
            fontSize: 10,
            labelFontStyle: "bold",
            valueFontStyle: "normal"
        };
        const opts = { ...defaultOptions, ...options };
        const lineHeight = opts.lineHeight;
        const columnGap = opts.columnGap;
        const spacing = opts.spacing;
        let x1 = margin.left;
        let x2 = opts.x2Start + spacing + columnGap;
        let y = opts.yStart;

        for (let i = 0; i < labelsData.length; i += 2) {
            // Draw first label-data pair
            const left = labelsData[i];
            if (enableInputs && left) {
                this.renderInput(left.label, left.data, x1, y, spacing);
            } else {
                this.renderText(left.label, left.data, x1, y, spacing, {
                    font: opts.font,
                    fontSize: opts.fontSize,
                    labelFontStyle: opts.labelFontStyle,
                    valueFontStyle: opts.valueFontStyle
                });
            }

            // Draw second label-data pair if it exists
            const right = labelsData[i + 1];
            if (enableInputs && right) {
                this.renderInput(right.label, right.data, x2, y, spacing);
            } else if (right) {
                this.renderText(right.label, right.data, x2, y, spacing, {
                    font: opts.font,
                    fontSize: opts.fontSize,
                    fontStyle: opts.fontStyle,
                    labelFontStyle: opts.labelFontStyle,
                    valueFontStyle: opts.valueFontStyle
                });
            }

            y += lineHeight;
        }
        return y;
    }
}
